import { useLocation, useSearchParams } from '@remix-run/react'
import mixpanel, { Mixpanel } from 'mixpanel-browser'
import { ReactNode, createContext, useContext, useEffect, useRef } from 'react'
import { useUserContext } from '~/core/context'
import { Toolbox } from '~/plugins/toolbox/shared'

const useInternal = () => {
  const { session } = useUserContext()

  const location = useLocation()
  const [searchParams] = useSearchParams()

  const searchParamsHash = Toolbox.Utility.createHash(
    JSON.stringify(searchParams),
  )

  const instance = useRef<Mixpanel>(null)

  const apiKey = import.meta.env.VITE_MIXPANEL_API_KEY
  const isDebug = import.meta.env.DEV
  const userId = session.user?.id

  const isAuthorised =
    Toolbox.Utility.isDefined(apiKey) &&
    // Toolbox.Utility.isDefined(userId) &&
    !session.isAdmin()

  const start = () => {
    try {
      mixpanel.init(apiKey, {
        debug: isDebug,
        ignore_dnt: true,
      })

      mixpanel.identify(userId)

      mixpanel.people.set({
        $user_id: userId,
        $email: session.user?.email,
        $name: session.user?.name,
      })

      instance.current = mixpanel
    } catch {
      // ignore
    }
  }

  const trackPath = (pathname: string) => {
    if (!instance.current || Toolbox.Utility.isNull(pathname)) {
      return
    }

    const REGEX_UUID =
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

    const pathNormalized = pathname.replace(REGEX_UUID, ':uuid')

    instance.current?.track('page.path', {
      path: pathname,
      route: pathNormalized,
      query: searchParams,
      queryString: searchParams.toString(),
    })
  }

  useEffect(() => {
    if (!isAuthorised) {
      return
    }

    start()
  }, [isAuthorised, userId])

  useEffect(() => {
    trackPath(location.pathname)
  }, [location.pathname, searchParams])

  const isActive = Toolbox.Utility.isDefined(instance.current)

  const track = (
    event_name: string,
    properties?: Parameters<typeof instance.current.track>[1],
  ) => {
    if (!isActive) {
      return
    }

    try {
      instance.current.track(event_name, properties)
    } catch (error) {
      // ignore
    }
  }

  return {
    isActive,
    mixpanel: instance.current,
    track,
  }
}

type MixpanelContext = ReturnType<typeof useInternal>

const MixpanelContext = createContext<MixpanelContext>(undefined)

export const MixpanelProvider = ({ children }: { children: ReactNode }) => {
  const value = useInternal()

  return (
    <MixpanelContext.Provider value={value}>
      {children}
    </MixpanelContext.Provider>
  )
}

export const useMixpanelContext = (): MixpanelContext => {
  const context = useContext(MixpanelContext)

  if (context === undefined) {
    throw new Error('useMixpanelContext must be used within a MixpanelProvider')
  }

  return context
}
